/**
 * Business Loan Insert API Service
 * Handles business loan (PSBL) application submission
 *
 * Refactored from: 3in1cms/bajajfinserv-web/ui.solid/src/lib/connectionManager/blinsert.ts
 * Logic preserved AS-IS, using Redux instead of SolidJS signals
 */

import type { IBlInsertApiRequest, IBlInsertApiResponse } from "../types/blInsertApiTypes";
import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";
import { logPageActionToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { getLoginState, commonInsertParameters } from "@repo/shared-utils/insert-params";
import { request } from "../core/request";
import { deleteFromCartApi } from "./cart.service";
import {
  API_RESPONSE_CODES,
  LOAN_TYPES_MAPPING,
  OPERATION_IDS,
  PSBL_EVENT_CODE_V4,
  PSBL_PROD_NAME,
  SME_EVENT_NAME,
  V5,
  HYBRID,
} from "./constants";

/**
 * Handle hybrid loan variant EMI amount
 * Internal helper for processing hybrid loan variant data
 */
function handleHybridLoanVariant(requestBody: IBlInsertApiRequest) {
  if (requestBody.loanVariant === HYBRID) {
    const dropline =
      typeof requestBody.droplineEMIAmt === "string" ? requestBody.droplineEMIAmt : undefined;
    const hybrid =
      typeof requestBody.hybridEmiAmt === "string" ? requestBody.hybridEmiAmt : undefined;
    requestBody.emiAmt = dropline ?? hybrid;
  }
}

/**
 * Insert business loan (PSBL) application
 *
 * @param data - Business loan application request data
 * @returns Promise with business loan insert response
 *
 * @example
 * ```typescript
 * const response = await psblInsert({
 *   mobileNo: "9876543210",
 *   journeyName: "PSBL_FRESH",
 *   loanAmt: "500000",
 *   tenureInMon: "36"
 * });
 * ```
 */
export const psblInsert = async (data: IBlInsertApiRequest): Promise<IBlInsertApiResponse> => {
  data.offerId = data.offerId ?? "";
  data.offerName = data.offerName ?? "";
  handleHybridLoanVariant(data);

  if (data.loanVariant) {
    data.loanVariant = LOAN_TYPES_MAPPING[data.loanVariant] ?? data.loanVariant;
  }

  try {
    if (data.mobileNo && data.appId) {
      await deleteFromCartApi({
        mobileNo: data.mobileNo,
        appId: data.appId,
        channel: "3in1",
      }).catch((deleteError: Error) => {
        logPageActionToNewRelic("PSBL_DELETE_CART_ERROR", {
          component: "PSBL Insert",
          pageUrl: typeof window !== "undefined" ? window.location.href : "",
          message: deleteError.message,
          debugInfo: JSON.stringify({
            loginState: getLoginState(),
            hasAToken:
              typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
            hasMobile: !!data.mobileNo,
          }),
        });
      });
    }

    const response = (await request({
      operationId: OPERATION_IDS.blInsert,
      requestData: {
        ...commonInsertParameters({
          appDtls: {
            custId: data.custId,
            profession: data.profession,
            subProfession: data.subProfession,
            custType: "Individual",
          },
        }),
        appVersion: V5,
        prodName: PSBL_PROD_NAME,
        eventCode: PSBL_EVENT_CODE_V4,
        eventName: SME_EVENT_NAME,
        ...data,
      },
    })) as IBlInsertApiResponse & { statusCode?: string };

    if (response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7001) {
      throw new Error(JSON.stringify(response));
    }
    return response;
  } catch (error) {
    logPageActionToNewRelic("PSBL_INSERT_ERROR", {
      component: "PSBL Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (error as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!data.mobileNo,
      }),
    });
    return {} as IBlInsertApiResponse;
  }
};
